import { useMsal } from '@azure/msal-react';
import {
  AccountCircle,
  Logout as LogoutIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import {
  AppBar,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  useTheme,
} from '@mui/material';
import { Brand } from 'components/Brand';
import { useContextUser } from 'context/user.context';
import React from 'react';

type HeaderProps = {
  handleSidebar: () => void;
};

export function Header({ handleSidebar }: HeaderProps) {
  const theme = useTheme();
  const { user } = useContextUser();
  const { instance } = useMsal();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);
  const menuId = 'user-menu';

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogOut = () => {
    handleMenuClose();
    instance.logoutRedirect();
  };

  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      id={menuId}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      sx={{
        '& .MuiMenu-paper': {
          minWidth: '150px',
          background: theme.palette.common.white,
        },
      }}
      open={isMenuOpen}
      onClose={handleMenuClose}
    >
      <MenuItem
        onClick={handleLogOut}
        sx={{
          fontSize: '.75rem',
          '&:hover': {
            background: theme.palette.secondary.main,
            color: theme.palette.common.white,
          },
        }}
      >
        <LogoutIcon sx={{ marginRight: theme.spacing(1), width: '16px' }} />
        Logout
      </MenuItem>
    </Menu>
  );

  return (
    <Box
      sx={{
        zIndex: theme.zIndex.drawer + 1,
        position: 'relative',
      }}
    >
      <AppBar position="fixed" elevation={0}>
        <Toolbar>
          <Box
            sx={{
              width: 150,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Brand mode="light" />
          </Box>
          <IconButton
            size="large"
            edge="start"
            color="inherit"
            aria-label="open drawer"
            sx={{ ml: theme.spacing(4) }}
            onClick={handleSidebar}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" component="span">
              Hi, <strong>{user?.displayName}</strong>
            </Typography>
            <IconButton
              size="large"
              edge="end"
              aria-label="account of current user"
              aria-controls={menuId}
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
              color="inherit"
              sx={{
                marginLeft: theme.spacing(2),
              }}
            >
              <AccountCircle />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>
      {renderMenu}
    </Box>
  );
}
